import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { CategorySelectGroups } from "@/components/category-select-groups";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod/v4";
import { useEffect, useState, useRef } from "react";
import { DndContext, closestCenter, type DragEndEvent } from "@dnd-kit/core";
import { SortableContext, verticalListSortingStrategy, useSortable, arrayMove } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { AutoExpandingTextarea } from "@/components/auto-expanding-textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { HelpCircle, Sparkles, TrendingDown, GripVertical, X, Plus, ListOrdered } from "lucide-react";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { frontendHabitSchema, type Category, type Goal } from "@shared/schema";

interface CreateHabitModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  goalId?: string;
}

const formSchema = frontendHabitSchema;
type FormInput = z.input<typeof formSchema>;
type FormData = z.output<typeof formSchema>;

type HabitType = "positive" | "negative" | null;

type SequenceStepDraft = {
  id: string;
  description: string;
  frequency: string;
  daysOfWeek: string[];
  timeOfDay: string;
  place: string;
};

const ATOMIC_TOOLTIPS: Record<string, { title: string; text: string }> = {
  neg_cue: {
    title: "The Cue — What triggers it?",
    text: "The cue is the signal that kicks off your habit loop. It can be a time of day, a location, an emotional state, a preceding action, or another person. Example: Feeling stressed at 3pm → reach for your phone. Identifying your cue is the first step to taking back control.",
  },
  neg_craving: {
    title: "The Craving — What do you really want?",
    text: "The craving is the motivational force behind the habit — not the behavior itself, but the feeling or state change you desire. You don't crave the cigarette; you crave the relief from stress. Understanding the real need lets you find healthier ways to meet it.",
  },
  neg_response: {
    title: "The Response — What do you actually do?",
    text: "The response is the habit itself — the thought or action you perform. The easier and more automatic the behavior, the harder it is to interrupt. Naming the exact behavior precisely helps you design a replacement that meets the same craving.",
  },
  neg_reward: {
    title: "The Reward — What do you get from it?",
    text: "The reward is what satisfies the craving and teaches your brain this loop is worth repeating. Rewards deliver pleasure, relief, or a feeling of completion. Understanding what you gain helps you find a satisfying substitute so the brain doesn't fight you.",
  },
  neg_change_cue: {
    title: "Change the Cue: Make it Invisible",
    text: "Reduce your exposure to the triggers that cause the habit. Redesign your environment so the cue disappears from your daily path. Where are you when it happens? What time? What surrounds you? Remove, hide, or restructure the environment to eliminate the signal before it fires.",
  },
  neg_change_craving: {
    title: "Change the Craving: Make it Unattractive",
    text: "Reframe how you think about the habit. Highlight the long-term costs rather than the short-term relief. Shift your identity — say 'I'm not that person anymore' rather than 'I'm trying to quit.' Linking the habit to negative outcomes makes the craving lose its pull.",
  },
  neg_change_response: {
    title: "Change the Response: Make it Difficult",
    text: "Add friction between you and the behavior. Increase the number of steps required to perform it — James Clear calls this 'increasing activation energy.' Lock your phone in a drawer, keep unhealthy food out of the house. The more effort required, the less automatic it becomes.",
  },
  neg_change_reward: {
    title: "Change the Reward: Make it Unsatisfying",
    text: "Create an immediate cost for the bad behavior. Use a commitment device, an accountability partner, or a habit contract that makes failing painful right now — not in the future. When there is an immediate negative consequence, the habit loses its appeal fast.",
  },
  pos_cue: {
    title: "Create the Cue: Make it Obvious",
    text: "Use an implementation intention: 'I will [DO THE HABIT] at [TIME] in [LOCATION].' Habit stack it after something you already do daily — 'After I pour my morning coffee, I will meditate for two minutes.' Design your environment so cues are visible and inescapable.",
  },
  pos_craving: {
    title: "Create the Craving: Make it Attractive",
    text: "Use temptation bundling — pair the habit you need with something you already enjoy. Join a group where the desired behavior is the norm. Create a motivation ritual by doing something enjoyable right before the habit. The more attractive the habit feels, the more you want to start.",
  },
  pos_response: {
    title: "The Response: Make it Easy",
    text: "Reduce friction to the lowest possible level. Use the Two-Minute Rule: scale the habit down until it takes less than two minutes to start — 'Exercise for 30 minutes' becomes 'Put on my workout clothes.' Getting started is everything; momentum carries you the rest of the way.",
  },
  pos_reward: {
    title: "The Reward: Make it Satisfying",
    text: "What gets rewarded gets repeated. Give yourself an immediate reward after completing the habit — celebrate it, track it on a visible streak, tell someone. Use a habit tracker so you can see your chain growing. And if you miss — never miss twice. Returning fast is the key skill.",
  },
};

interface AtomicRowProps {
  tooltipKey: string;
  label: string;
  placeholder: string;
  value: string;
  onChange: (v: string) => void;
  activeTooltip: string | null;
  onTooltipToggle: (key: string | null) => void;
}

function AtomicRow({ tooltipKey, label, placeholder, value, onChange, activeTooltip, onTooltipToggle }: AtomicRowProps) {
  const tip = ATOMIC_TOOLTIPS[tooltipKey];
  const isOpen = activeTooltip === tooltipKey;
  return (
    <div className="space-y-1.5">
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-1 w-44 flex-shrink-0">
          <span className="text-sm font-medium leading-tight">{label}</span>
          <button
            type="button"
            onClick={() => onTooltipToggle(isOpen ? null : tooltipKey)}
            className="text-muted-foreground hover:text-foreground transition-colors flex-shrink-0"
          >
            <HelpCircle className="w-3.5 h-3.5" />
          </button>
        </div>
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="flex-1 h-8 text-sm"
        />
      </div>
      {isOpen && tip && (
        <div className="ml-44 pl-3 py-2 pr-3 bg-muted/70 border border-border rounded-md text-xs space-y-1">
          <p className="font-semibold text-foreground">{tip.title}</p>
          <p className="text-muted-foreground leading-relaxed">{tip.text}</p>
        </div>
      )}
    </div>
  );
}

const DAYS_SHORT = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

function SortableStepRow({
  step,
  onUpdate,
  onDelete,
}: {
  step: SequenceStepDraft;
  onUpdate: (id: string, updates: Partial<SequenceStepDraft>) => void;
  onDelete: (id: string) => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: step.id });
  const style = { transform: CSS.Transform.toString(transform), transition, opacity: isDragging ? 0.5 : 1 };
  const showDays = step.frequency === "weekly" || step.frequency === "biweekly";

  return (
    <div ref={setNodeRef} style={style} className="flex flex-col gap-2 p-3 border border-border rounded-lg bg-background">
      <div className="flex items-center gap-2">
        <button
          type="button"
          className="cursor-grab text-muted-foreground hover:text-foreground touch-none flex-shrink-0"
          {...attributes}
          {...listeners}
        >
          <GripVertical className="w-4 h-4" />
        </button>
        <Input
          value={step.description}
          onChange={(e) => onUpdate(step.id, { description: e.target.value })}
          placeholder="Describe this step..."
          className="flex-1 h-8 text-sm"
        />
        <button
          type="button"
          onClick={() => onDelete(step.id)}
          className="text-muted-foreground hover:text-destructive transition-colors flex-shrink-0"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
      <div className="flex flex-wrap gap-2 pl-6">
        <Select
          value={step.frequency}
          onValueChange={(v) => onUpdate(step.id, { frequency: v, daysOfWeek: [] })}
        >
          <SelectTrigger className="h-7 text-xs w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="daily">Daily</SelectItem>
            <SelectItem value="weekly">Weekly</SelectItem>
            <SelectItem value="biweekly">Bi-weekly</SelectItem>
            <SelectItem value="monthly">Monthly</SelectItem>
            <SelectItem value="quarterly">Quarterly</SelectItem>
            <SelectItem value="annually">Annually</SelectItem>
          </SelectContent>
        </Select>
        {showDays && (
          <div className="flex gap-1 flex-wrap">
            {DAYS_SHORT.map((day) => (
              <button
                type="button"
                key={day}
                onClick={() => {
                  const current = step.daysOfWeek || [];
                  const updated = current.includes(day)
                    ? current.filter((d) => d !== day)
                    : [...current, day];
                  onUpdate(step.id, { daysOfWeek: updated });
                }}
                className={`px-1.5 py-0.5 text-xs rounded border transition-colors ${
                  (step.daysOfWeek || []).includes(day)
                    ? "bg-primary text-primary-foreground border-primary"
                    : "bg-background border-border text-muted-foreground hover:border-primary"
                }`}
              >
                {day}
              </button>
            ))}
          </div>
        )}
        <Input
          type="time"
          value={step.timeOfDay || ""}
          onChange={(e) => onUpdate(step.id, { timeOfDay: e.target.value })}
          className="h-7 text-xs w-28"
        />
        <Input
          value={step.place || ""}
          onChange={(e) => onUpdate(step.id, { place: e.target.value })}
          placeholder="Place..."
          className="h-7 text-xs w-32"
        />
      </div>
    </div>
  );
}

function makeStepId() {
  return `draft-${Math.random().toString(36).slice(2)}`;
}

export default function CreateHabitModal({ open, onOpenChange, goalId }: CreateHabitModalProps) {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [selectedGoalIds, setSelectedGoalIds] = useState<string[]>([]);
  const [isHabitStack, setIsHabitStack] = useState(false);
  const [habitStackStartDays, setHabitStackStartDays] = useState<number | null>(null);
  const [sequenceSuccessing, setSequenceSuccessing] = useState(false);
  const [showHabitStackHelp, setShowHabitStackHelp] = useState(false);
  const [showSequenceHelp, setShowSequenceHelp] = useState(false);

  // Habit Sequencing state
  const [isHabitSequencing, setIsHabitSequencing] = useState(false);
  const [sequenceSteps, setSequenceSteps] = useState<SequenceStepDraft[]>([]);

  // Atomic Habits state
  const [habitType, setHabitType] = useState<HabitType>(null);
  const [activeTooltip, setActiveTooltip] = useState<string | null>(null);
  const [atomicCue, setAtomicCue] = useState("");
  const [atomicCraving, setAtomicCraving] = useState("");
  const [atomicResponse, setAtomicResponse] = useState("");
  const [atomicReward, setAtomicReward] = useState("");
  const [atomicChangeCue, setAtomicChangeCue] = useState("");
  const [atomicChangeCraving, setAtomicChangeCraving] = useState("");
  const [atomicChangeResponse, setAtomicChangeResponse] = useState("");
  const [atomicChangeReward, setAtomicChangeReward] = useState("");

  const { data: categories = [] } = useQuery<Category[]>({
    queryKey: ["/api/categories?includeGoalsOnly=true"],
  });

  const { data: allGoals = [] } = useQuery<Goal[]>({
    queryKey: ["/api/goals"],
  });

  const goals = allGoals.filter(g =>
    g.status !== "completed" &&
    g.progress < 100 &&
    !g.isFutureGoal && g.status !== "future"
  );

  const form = useForm<FormInput, any, FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: "",
      description: "",
      category: "Health",
      frequency: "daily",
      target: undefined,
      isActive: true,
      isHabitStack: false,
      habitStackStartDays: undefined,
      habitStackPhase: 1,
      habitStackCurrentTarget: undefined,
    },
  });

  useEffect(() => {
    if (open) {
      setIsHabitStack(false);
      setHabitStackStartDays(null);
      setSequenceSuccessing(false);
      setIsHabitSequencing(false);
      setSequenceSteps([]);
      setHabitType(null);
      setActiveTooltip(null);
      setAtomicCue("");
      setAtomicCraving("");
      setAtomicResponse("");
      setAtomicReward("");
      setAtomicChangeCue("");
      setAtomicChangeCraving("");
      setAtomicChangeResponse("");
      setAtomicChangeReward("");
      if (goalId) {
        setSelectedGoalIds([goalId]);
      } else {
        setSelectedGoalIds([]);
      }
    } else {
      // Modal closed — clear key so the next open starts a fresh submission.
      idempotencyKeyRef.current = null;
    }
  }, [open, goalId]);

  const createHabitMutation = useMutation({
    mutationFn: async (data: FormData) => {
      const habitData = {
        ...data,
        isHabitStack,
        habitStackStartDays: isHabitStack ? habitStackStartDays : null,
        habitStackPhase: 1,
        habitStackCurrentTarget: isHabitStack ? habitStackStartDays : null,
        target: isHabitStack && habitStackStartDays ? habitStackStartDays : data.target,
        sequenceSuccessing,
        isHabitSequencing,
        habitType: habitType ?? null,
        atomicCue: atomicCue || null,
        atomicCraving: atomicCraving || null,
        atomicResponse: atomicResponse || null,
        atomicReward: atomicReward || null,
        atomicChangeCue: atomicChangeCue || null,
        atomicChangeCraving: atomicChangeCraving || null,
        atomicChangeResponse: atomicChangeResponse || null,
        atomicChangeReward: atomicChangeReward || null,
      };
      // Generate a key once per submission attempt; reuse it on network-
      // error retries so the server can deduplicate the request.
      if (!idempotencyKeyRef.current) {
        idempotencyKeyRef.current = crypto.randomUUID();
      }
      const response = await apiRequest("POST", "/api/habits", habitData, {
        "Idempotency-Key": idempotencyKeyRef.current,
      });
      const newHabit = await response.json();

      if (selectedGoalIds.length > 0) {
        await apiRequest("PUT", `/api/habits/${newHabit.id}/goals`, { goalIds: selectedGoalIds });
      }

      if (isHabitSequencing && sequenceSteps.length > 0) {
        await apiRequest("PUT", `/api/habits/${newHabit.id}/sequences`, {
          steps: sequenceSteps.map((s, i) => ({
            description: s.description,
            frequency: s.frequency,
            daysOfWeek: s.daysOfWeek,
            timeOfDay: s.timeOfDay || null,
            place: s.place || null,
            sortOrder: i,
          })),
        });
      }

      return newHabit;
    },
    onSuccess: () => {
      // Submission confirmed — discard the key so a future open starts fresh.
      idempotencyKeyRef.current = null;
      queryClient.invalidateQueries({ queryKey: ["/api/habits"] });
      queryClient.invalidateQueries({ queryKey: ["/api/goals"] });
      selectedGoalIds.forEach(gid => {
        queryClient.invalidateQueries({ queryKey: ["/api/habits", gid] });
      });
      queryClient.invalidateQueries({ queryKey: ["/api/stats/dashboard"] });
      queryClient.invalidateQueries({ queryKey: ["/api/stats/habits"] });
      queryClient.invalidateQueries({ queryKey: ["/api/activities"] });

      form.reset();
      setSelectedGoalIds([]);
      onOpenChange(false);
    },
    onError: (error: Error) => {
      console.error("Failed to create habit:", error);
      toast({
        title: "Failed to create habit",
        description: error.message || "Please try again",
        variant: "destructive",
      });
    },
  });

  // Idempotency key: generated once per submission attempt and reused on
  // network-error retries so the server can deduplicate. Cleared after a
  // confirmed success or when the modal closes (so the next open starts fresh).
  const idempotencyKeyRef = useRef<string | null>(null);

  const submitLockRef = useRef(false);
  useEffect(() => {
    if (!createHabitMutation.isPending) submitLockRef.current = false;
  }, [createHabitMutation.isPending]);

  const onSubmit = (data: FormData) => {
    if (submitLockRef.current || createHabitMutation.isPending) return;
    submitLockRef.current = true;
    createHabitMutation.mutate(data);
  };

  const toggleGoalSelection = (goalIdToToggle: string) => {
    setSelectedGoalIds(prev =>
      prev.includes(goalIdToToggle)
        ? prev.filter(id => id !== goalIdToToggle)
        : [...prev, goalIdToToggle]
    );
  };

  const frequency = useWatch({
    control: form.control,
    name: "frequency",
    defaultValue: "daily",
  });

  const getPlaceholder = () => {
    switch (frequency) {
      case "daily": return "Enter target (1-365)";
      case "weekly": return "Enter target (1-52)";
      case "monthly": return "Enter target (1-12)";
      case "quarterly": return "Enter target (1-4)";
      default: return "Enter target";
    }
  };

  const getMaxValue = () => {
    switch (frequency) {
      case "daily": return 365;
      case "weekly": return 52;
      case "monthly": return 12;
      case "quarterly": return 4;
      default: return 365;
    }
  };

  const addStep = () => {
    setSequenceSteps(prev => [...prev, {
      id: makeStepId(),
      description: "",
      frequency: "daily",
      daysOfWeek: [],
      timeOfDay: "",
      place: "",
    }]);
  };

  const updateStep = (id: string, updates: Partial<SequenceStepDraft>) => {
    setSequenceSteps(prev => prev.map(s => s.id === id ? { ...s, ...updates } : s));
  };

  const deleteStep = (id: string) => {
    setSequenceSteps(prev => prev.filter(s => s.id !== id));
  };

  const handleStepDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (over && active.id !== over.id) {
      setSequenceSteps(prev => {
        const oldIndex = prev.findIndex(s => s.id === active.id);
        const newIndex = prev.findIndex(s => s.id === over.id);
        return arrayMove(prev, oldIndex, newIndex);
      });
    }
  };

  const negStep1Rows = [
    { key: "neg_cue", label: "The Cue", placeholder: "What triggers this habit?", value: atomicCue, onChange: setAtomicCue },
    { key: "neg_craving", label: "The Craving", placeholder: "What do you really desire?", value: atomicCraving, onChange: setAtomicCraving },
    { key: "neg_response", label: "The Response", placeholder: "What is the actual behavior?", value: atomicResponse, onChange: setAtomicResponse },
    { key: "neg_reward", label: "The Reward", placeholder: "What do you get from it?", value: atomicReward, onChange: setAtomicReward },
  ];

  const negStep2Rows = [
    { key: "neg_change_cue", label: "Change the Cue", placeholder: "Where are you? What's your environment?", value: atomicChangeCue, onChange: setAtomicChangeCue },
    { key: "neg_change_craving", label: "Change the Craving", placeholder: "How will you shift your identity/beliefs?", value: atomicChangeCraving, onChange: setAtomicChangeCraving },
    { key: "neg_change_response", label: "Change the Response", placeholder: "What will you replace the behavior with?", value: atomicChangeResponse, onChange: setAtomicChangeResponse },
    { key: "neg_change_reward", label: "Change the Reward", placeholder: "What healthier reward will you substitute?", value: atomicChangeReward, onChange: setAtomicChangeReward },
  ];

  const posStep1Rows = [
    { key: "pos_cue", label: "Create the Cue", placeholder: "Where, when, what triggers this habit?", value: atomicCue, onChange: setAtomicCue },
    { key: "pos_craving", label: "Create the Craving", placeholder: "What feeling are you trying to achieve?", value: atomicCraving, onChange: setAtomicCraving },
    { key: "pos_response", label: "The Response", placeholder: "What action do you need to take?", value: atomicResponse, onChange: setAtomicResponse },
    { key: "pos_reward", label: "The Reward", placeholder: "What do you get when you complete it?", value: atomicReward, onChange: setAtomicReward },
  ];

  return (
    <>
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto" data-testid="modal-create-habit">
        <DialogHeader>
          <DialogTitle>Create New Habit</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">

            {/* Habit Type Toggle */}
            <div className="space-y-1.5">
              <FormLabel>Habit Type</FormLabel>
              <div className="flex rounded-lg border border-border overflow-hidden">
                <button
                  type="button"
                  onClick={() => { setHabitType("positive"); setActiveTooltip(null); }}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium transition-colors ${
                    habitType === "positive"
                      ? "bg-primary text-primary-foreground"
                      : "bg-background text-muted-foreground hover:bg-muted"
                  }`}
                  data-testid="button-habit-type-positive"
                >
                  <Sparkles className="w-4 h-4" />
                  Create a Positive Habit
                </button>
                <button
                  type="button"
                  onClick={() => { setHabitType("negative"); setActiveTooltip(null); }}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium border-l border-border transition-colors ${
                    habitType === "negative"
                      ? "bg-destructive text-destructive-foreground"
                      : "bg-background text-muted-foreground hover:bg-muted"
                  }`}
                  data-testid="button-habit-type-negative"
                >
                  <TrendingDown className="w-4 h-4" />
                  Break a Negative Habit
                </button>
              </div>
            </div>

            {/* Habit Name */}
            <FormField
              control={form.control}
              name="name"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Habit Name</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="What habit do you want to build?"
                      {...field}
                      data-testid="input-habit-name"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Description */}
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description (Optional)</FormLabel>
                  <FormControl>
                    <AutoExpandingTextarea
                      placeholder="Why is this habit important to you?"
                      minRows={2}
                      maxRows={10}
                      {...field}
                      value={field.value || ""}
                      data-testid="textarea-habit-description"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Atomic Habits Framework */}
            {habitType && (
              <div className="space-y-4 rounded-lg border border-border p-4 bg-muted/20">
                <div className="flex items-center gap-2">
                  <div className="h-px flex-1 bg-border" />
                  <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                    Atomic Habits Framework
                  </span>
                  <div className="h-px flex-1 bg-border" />
                </div>

                {habitType === "negative" ? (
                  <>
                    {/* Negative — Step 1 */}
                    <div className="space-y-3">
                      <div className="space-y-0.5">
                        <p className="text-sm font-semibold">Step 1: Understand Your Current Habit Loop</p>
                        <p className="text-xs text-muted-foreground">Identify the four parts of the habit you want to break.</p>
                      </div>
                      {negStep1Rows.map((row) => (
                        <AtomicRow
                          key={row.key}
                          tooltipKey={row.key}
                          label={row.label}
                          placeholder={row.placeholder}
                          value={row.value}
                          onChange={row.onChange}
                          activeTooltip={activeTooltip}
                          onTooltipToggle={setActiveTooltip}
                        />
                      ))}
                    </div>

                    {/* Negative — Step 2 */}
                    <div className="space-y-3 pt-2 border-t border-border">
                      <div className="space-y-0.5">
                        <p className="text-sm font-semibold">Step 2: Your Plan to Break It</p>
                        <p className="text-xs text-muted-foreground">Use the four laws of behaviour change — inverted — to dismantle each part of the loop.</p>
                      </div>
                      {negStep2Rows.map((row) => (
                        <AtomicRow
                          key={row.key}
                          tooltipKey={row.key}
                          label={row.label}
                          placeholder={row.placeholder}
                          value={row.value}
                          onChange={row.onChange}
                          activeTooltip={activeTooltip}
                          onTooltipToggle={setActiveTooltip}
                        />
                      ))}
                    </div>
                  </>
                ) : (
                  /* Positive — Design Your Habit Loop */
                  <div className="space-y-3">
                    <div className="space-y-0.5">
                      <p className="text-sm font-semibold">Design Your New Habit Loop</p>
                      <p className="text-xs text-muted-foreground">Use the four laws of behaviour change to make your habit obvious, attractive, easy, and satisfying.</p>
                    </div>
                    {posStep1Rows.map((row) => (
                      <AtomicRow
                        key={row.key}
                        tooltipKey={row.key}
                        label={row.label}
                        placeholder={row.placeholder}
                        value={row.value}
                        onChange={row.onChange}
                        activeTooltip={activeTooltip}
                        onTooltipToggle={setActiveTooltip}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Habit Stack */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <FormLabel className="text-base">Habit Stack</FormLabel>
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); setShowHabitStackHelp(true); }}
                    className="text-muted-foreground hover:text-foreground transition-colors"
                    data-testid="button-help-habit-stack"
                  >
                    <HelpCircle className="w-4 h-4" />
                  </button>
                </div>
                <Switch
                  checked={isHabitStack}
                  onCheckedChange={(checked) => {
                    setIsHabitStack(checked);
                    if (!checked) setHabitStackStartDays(null);
                    if (checked) form.setValue("frequency", "daily");
                  }}
                  data-testid="switch-habit-stack"
                />
              </div>
              <p className="text-sm text-muted-foreground">Build up gradually - start small and increase over time</p>
              {isHabitStack && (
                <div className="pl-2 border-l-2 border-primary/20 space-y-2">
                  <FormLabel>Start with</FormLabel>
                  <ToggleGroup type="single" value={habitStackStartDays?.toString() || ""} onValueChange={(val) => setHabitStackStartDays(val ? parseInt(val) : null)}>
                    <ToggleGroupItem value="1" className="flex-1">1 Day</ToggleGroupItem>
                    <ToggleGroupItem value="2" className="flex-1">2 Days</ToggleGroupItem>
                    <ToggleGroupItem value="3" className="flex-1">3 Days</ToggleGroupItem>
                  </ToggleGroup>
                  <p className="text-xs text-muted-foreground">
                    After completing {habitStackStartDays || "N"} consecutive days, you'll be prompted to extend
                  </p>
                </div>
              )}
            </div>

            {/* Habit Sequencing */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <ListOrdered className="w-4 h-4 text-muted-foreground" />
                  <FormLabel className="text-base">Habit Sequencing</FormLabel>
                </div>
                <Switch
                  checked={isHabitSequencing}
                  onCheckedChange={(checked) => {
                    setIsHabitSequencing(checked);
                    if (!checked) setSequenceSteps([]);
                  }}
                  data-testid="switch-habit-sequencing"
                />
              </div>
              <p className="text-sm text-muted-foreground">Define ordered steps — each must be completed before marking the habit done</p>

              {isHabitSequencing && (
                <div className="pl-2 border-l-2 border-primary/20 space-y-3">
                  <DndContext collisionDetection={closestCenter} onDragEnd={handleStepDragEnd}>
                    <SortableContext items={sequenceSteps.map(s => s.id)} strategy={verticalListSortingStrategy}>
                      <div className="space-y-2">
                        {sequenceSteps.map((step) => (
                          <SortableStepRow
                            key={step.id}
                            step={step}
                            onUpdate={updateStep}
                            onDelete={deleteStep}
                          />
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                  {sequenceSteps.length === 0 && (
                    <p className="text-xs text-muted-foreground italic">No steps yet. Add steps below.</p>
                  )}
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={addStep}
                    className="w-full"
                    data-testid="button-add-sequence-step"
                  >
                    <Plus className="w-3.5 h-3.5 mr-1.5" />
                    Add Step
                  </Button>
                </div>
              )}
            </div>

            {/* Sequence Successing */}
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-1.5">
                  <FormLabel className="text-base">Sequence Successing</FormLabel>
                  <button
                    type="button"
                    onClick={(e) => { e.preventDefault(); setShowSequenceHelp(true); }}
                    className="text-muted-foreground hover:text-foreground transition-colors"
                    data-testid="button-help-sequence-successing"
                  >
                    <HelpCircle className="w-4 h-4" />
                  </button>
                </div>
                <Switch
                  checked={sequenceSuccessing}
                  onCheckedChange={setSequenceSuccessing}
                  data-testid="switch-sequence-successing"
                />
              </div>
              <p className="text-sm text-muted-foreground">Miss a day and the sequence resets - train yourself not to break the chain</p>
            </div>

            {/* Category & Frequency */}
            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Category</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value}>
                      <FormControl>
                        <SelectTrigger data-testid="select-habit-category">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {categories.length === 0 ? (
                          <SelectItem value="Uncategorized">Uncategorized</SelectItem>
                        ) : (
                          <CategorySelectGroups categories={categories} />
                        )}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="frequency"
                render={({ field }) => (
                  <FormItem className={isHabitStack ? "opacity-50" : ""}>
                    <FormLabel>Frequency</FormLabel>
                    <Select onValueChange={field.onChange} defaultValue={field.value} value={field.value} disabled={isHabitStack}>
                      <FormControl>
                        <SelectTrigger data-testid="select-habit-frequency">
                          <SelectValue placeholder="Select" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="daily">Daily</SelectItem>
                        <SelectItem value="weekly">Weekly</SelectItem>
                        <SelectItem value="monthly">Monthly</SelectItem>
                        <SelectItem value="quarterly">Quarterly</SelectItem>
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Target */}
            <FormField
              control={form.control}
              name="target"
              render={({ field }) => (
                <FormItem className={isHabitStack ? "opacity-50" : ""}>
                  <FormLabel>Target Times</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      min={1}
                      max={getMaxValue()}
                      placeholder={isHabitStack ? "Managed by Habit Stack" : getPlaceholder()}
                      {...field}
                      value={field.value || ""}
                      onChange={(e) => field.onChange(e.target.value ? parseInt(e.target.value) : undefined)}
                      disabled={isHabitStack}
                      data-testid="input-habit-target"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Linked Goals */}
            <div className="space-y-2">
              <FormLabel>Linked Goals (Optional)</FormLabel>
              <div className="text-sm text-muted-foreground mb-2">
                Link this habit to goals to help track progress
              </div>
              {goals.length === 0 ? (
                <div className="text-sm text-muted-foreground italic p-3 border border-dashed rounded">
                  No goals available. Create a goal first to link it to this habit.
                </div>
              ) : (
                <div className="space-y-2 max-h-48 overflow-y-auto p-3 border rounded" data-testid="goals-selection-create">
                  {goals.map((goal) => (
                    <div key={goal.id} className="flex items-center space-x-2">
                      <Checkbox
                        id={`create-goal-${goal.id}`}
                        checked={selectedGoalIds.includes(goal.id)}
                        onCheckedChange={() => toggleGoalSelection(goal.id)}
                        data-testid={`checkbox-create-goal-${goal.id}`}
                      />
                      <label
                        htmlFor={`create-goal-${goal.id}`}
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer flex-1"
                      >
                        {goal.title}
                        {goal.category && (
                          <Badge variant="outline" className="ml-2 text-xs">
                            {goal.category}
                          </Badge>
                        )}
                      </label>
                    </div>
                  ))}
                </div>
              )}
              {selectedGoalIds.length > 0 && (
                <div className="text-sm text-muted-foreground">
                  {selectedGoalIds.length} goal{selectedGoalIds.length !== 1 ? "s" : ""} selected
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end space-x-3 pt-4 border-t border-border">
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                data-testid="button-cancel-habit"
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={submitLockRef.current || createHabitMutation.isPending || (isHabitStack && !habitStackStartDays)}
                className="bg-primary hover:bg-primary/90 text-primary-foreground"
                data-testid="button-submit-habit"
              >
                {createHabitMutation.isPending ? "Creating..." : "Create Habit"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>

    <Dialog open={showHabitStackHelp} onOpenChange={setShowHabitStackHelp}>
      <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle>What is Habit Stack?</DialogTitle>
          <DialogDescription>Understanding the gradual habit building system</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm text-foreground">
          <p>
            <strong>Habit Stack</strong> helps you build habits gradually instead of jumping straight to a high target. Research shows that starting small and progressively increasing makes habits stick.
          </p>
          <div className="space-y-2">
            <p className="font-semibold">How it works:</p>
            <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
              <li><strong>Phase 1:</strong> Start with 1, 2, or 3 days. Complete these consecutive days to prove you can do it.</li>
              <li><strong>Phase 2:</strong> Once you succeed, choose to extend to 3, 4, or 5 days. This gradually increases the challenge.</li>
              <li><strong>Phase 3+:</strong> After Phase 2, pick any custom target to continue growing at your own pace.</li>
            </ul>
          </div>
          <div className="space-y-2">
            <p className="font-semibold">Why use it:</p>
            <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
              <li>Prevents overwhelm by starting with achievable goals</li>
              <li>Builds confidence through small wins before increasing difficulty</li>
              <li>Creates momentum - each phase success motivates the next</li>
              <li>Perfect for habits you've struggled to maintain in the past</li>
            </ul>
          </div>
          <p className="text-muted-foreground italic">
            Example: Want to exercise daily? Start with just 1 day per week. Once that feels easy, bump to 3, then 5, then daily.
          </p>
        </div>
        <Button onClick={() => setShowHabitStackHelp(false)} className="w-full mt-2">Got it!</Button>
      </DialogContent>
    </Dialog>

    <Dialog open={showSequenceHelp} onOpenChange={setShowSequenceHelp}>
      <DialogContent className="w-screen h-screen max-w-none max-h-none m-0 rounded-none overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <DialogHeader>
          <DialogTitle>What is Sequence Successing?</DialogTitle>
          <DialogDescription>Understanding the streak-based accountability system</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm text-foreground">
          <p>
            <strong>Sequence Successing</strong> is a strict accountability tool that trains you to never miss a day. When enabled, missing even one day resets your entire sequence back to zero.
          </p>
          <div className="space-y-2">
            <p className="font-semibold">How it works:</p>
            <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
              <li>Each day you complete the habit, your sequence count grows</li>
              <li>If you miss a day, the missed day shows with a <span className="text-red-500 font-semibold">red background</span> so you can clearly see where you broke the chain</li>
              <li>After a missed day, your sequence counter resets and starts over from day 1</li>
              <li>Your goal is to complete the full target number of days without any breaks</li>
            </ul>
          </div>
          <div className="space-y-2">
            <p className="font-semibold">Why use it:</p>
            <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
              <li>Creates strong accountability - the red markers are a visual reminder</li>
              <li>Trains consistency - knowing a miss resets everything motivates you to show up</li>
              <li>Builds mental toughness and discipline over time</li>
              <li>Great for habits where consistency matters more than total count (meditation, exercise, study)</li>
            </ul>
          </div>
          <p className="text-muted-foreground italic">
            Example: If your habit target is 3 days but you only complete 2 days in a row and miss day 3, day 3 turns red and your sequence starts over on day 4. You need to complete 3 consecutive days without any gaps.
          </p>
        </div>
        <Button onClick={() => setShowSequenceHelp(false)} className="w-full mt-2">Got it!</Button>
      </DialogContent>
    </Dialog>
    </>
  );
}
